import {
  IBooleanFormatConfig,
  ICustomDateFormatConfig,
  ICustomNumberFormatConfig,
  IFormatterDataModel,
  IStringFormatConfig,
} from '../../../data-models/formatter.data-model';
import { StringTransform } from '../../../util/formatters/StringFormatters';
import { getFormatterExample } from '../../../components/FormatterSelect/formatter-meta';
import { applyDateFormat, CURRENT_DATE_FORMAT_ID, matchDateFormatOption } from './date-format-options';

export function isEditableFormatterType(type: string | undefined): boolean {
  return (
    type === 'number' || type === 'currency' || type === 'string' || type === 'date' || type === 'boolean'
  );
}

export interface FormatterFormViewModel {
  id: string;
  type: string;
  example: string;
  displayName?: string;
  description?: string;
  // number / currency config
  minDecimals?: number;
  maxDecimals?: number;
  shortNotation?: boolean;
  suffix?: string;
  zeroValue?: string;
  negativeValue?: string;
  // shared (number & date)
  invalidValue?: string;
  // shared (number, string, date & boolean)
  noValue?: string;
  // string config
  emptyValue?: string;
  transforms?: Array<keyof typeof StringTransform>;
  // date config
  format?: string; // a DATE_FORMAT_OPTIONS id, or CURRENT_DATE_FORMAT_ID for an unlisted shape
  timeZone?: string; // '' (the viewer's zone) or 'UTC'
  clock?: string; // '' (locale default), '12' or '24' — see formViewModelToFormatter
  // boolean config
  trueValue?: string;
  falseValue?: string;
}

export function formatterToFormViewModel(model: IFormatterDataModel<unknown>): FormatterFormViewModel {
  const vm: FormatterFormViewModel = {
    id: model.id,
    type: model.type ?? 'string',
    example: getFormatterExample(model),
    // The backfill migration defaults displayName to the id; mirror that for rows
    // predating it (or created before the columns existed).
    displayName: model.displayName ?? model.id,
    description: model.description,
  };

  if (model.type === 'number' || model.type === 'currency') {
    const config = (model.config ?? {}) as ICustomNumberFormatConfig;
    vm.minDecimals = config.minimumFractionDigits;
    vm.maxDecimals = config.maximumFractionDigits;
    vm.shortNotation = config.notation === 'compact';
    vm.suffix = config.suffix;
    vm.noValue = config.noValue;
    vm.zeroValue = config.zeroValue;
    vm.negativeValue = config.negativeValue;
    vm.invalidValue = config.invalidValue;
  } else if (model.type === 'string') {
    const config = (model.config ?? {}) as IStringFormatConfig;
    vm.noValue = config.noValue;
    vm.emptyValue = config.emptyValue;
    vm.transforms = config.transforms;
  } else if (model.type === 'date') {
    const config = (model.config ?? {}) as ICustomDateFormatConfig;
    vm.format = matchDateFormatOption(config) ?? CURRENT_DATE_FORMAT_ID;
    vm.timeZone = config.timeZone ?? ''; // '' is the "viewer's time zone" choice
    vm.clock = config.hour12 == null ? '' : config.hour12 ? '12' : '24';
    vm.noValue = config.noValue;
    vm.invalidValue = config.invalidValue;
  } else if (model.type === 'boolean') {
    const config = (model.config ?? {}) as IBooleanFormatConfig;
    vm.trueValue = config.trueValue;
    vm.falseValue = config.falseValue;
    vm.noValue = config.noValue;
  }

  return vm;
}

/**
 * Merges the form values into the original model's config instead of rebuilding it,
 * so config keys the form does not expose (style, currency, roundingMode, excelStyle,
 * extends, ...) survive an edit round-trip. Cleared fields fall back to the formatter's
 * default behavior (the key is removed from the config).
 */
export function formViewModelToFormatter(
  vm: FormatterFormViewModel,
  original: IFormatterDataModel<unknown>
): IFormatterDataModel<unknown> {
  if (original.type === 'number' || original.type === 'currency') {
    const config = { ...((original.config ?? {}) as ICustomNumberFormatConfig) };
    setOrDelete(config, 'minimumFractionDigits', toOptionalNumber(vm.minDecimals));
    setOrDelete(config, 'maximumFractionDigits', toOptionalNumber(vm.maxDecimals));
    if (
      config.minimumFractionDigits != null &&
      config.maximumFractionDigits != null &&
      config.maximumFractionDigits < config.minimumFractionDigits
    ) {
      // Intl.NumberFormat throws a RangeError for min > max, and formatters are compiled
      // eagerly at boot — persisting such a config would break app startup for the tenant.
      config.maximumFractionDigits = config.minimumFractionDigits;
    }
    if (vm.shortNotation) {
      config.notation = 'compact';
      config.compactDisplay = config.compactDisplay ?? 'short';
    } else if (config.notation === 'compact') {
      delete config.notation;
      delete config.compactDisplay;
    }
    setOrDelete(config, 'suffix', toOptionalString(vm.suffix));
    setOrDelete(config, 'noValue', toOptionalString(vm.noValue));
    setOrDelete(config, 'zeroValue', toOptionalString(vm.zeroValue));
    setOrDelete(config, 'negativeValue', toOptionalString(vm.negativeValue));
    setOrDelete(config, 'invalidValue', toOptionalString(vm.invalidValue));
    return applyDisplayMeta({ ...original, config }, vm);
  }

  if (original.type === 'string') {
    const config = { ...((original.config ?? {}) as IStringFormatConfig) };
    setOrDelete(config, 'noValue', toOptionalString(vm.noValue));
    setOrDelete(config, 'emptyValue', toOptionalString(vm.emptyValue));
    setOrDelete(config, 'transforms', vm.transforms?.length ? vm.transforms : undefined);
    return applyDisplayMeta({ ...original, config }, vm);
  }

  if (original.type === 'date') {
    const config = { ...((original.config ?? {}) as ICustomDateFormatConfig) };
    // Replaces every shape key at once, so the dateStyle and component families can never mix —
    // Intl.DateTimeFormat throws on that, and FormatterService compiles formatters at boot.
    // The pinned "current format" leaves the stored shape untouched.
    applyDateFormat(config, vm.format);
    // Any IANA zone is passed through, not just the two the select offers, so a hand-written
    // config keeps its zone when some other field is edited.
    setOrDelete(config, 'timeZone', toOptionalString(vm.timeZone));
    // The form keeps a third "locale default" choice because dropping the key is not the same as
    // hour12: false — the seeded `time` formatter is 24h only because the key is there.
    setOrDelete(config, 'hour12', vm.clock === '12' ? true : vm.clock === '24' ? false : undefined);
    setOrDelete(config, 'noValue', toOptionalString(vm.noValue));
    setOrDelete(config, 'invalidValue', toOptionalString(vm.invalidValue));
    return applyDisplayMeta({ ...original, config }, vm);
  }

  if (original.type === 'boolean') {
    const config = { ...((original.config ?? {}) as IBooleanFormatConfig) };
    // createBooleanFormatter returns these verbatim, so a deleted key makes the formatter return
    // undefined rather than a string: keep all three, empty when cleared.
    config.trueValue = vm.trueValue ?? '';
    config.falseValue = vm.falseValue ?? '';
    config.noValue = vm.noValue ?? '';
    return applyDisplayMeta({ ...original, config }, vm);
  }

  return original;
}

function applyDisplayMeta(
  model: IFormatterDataModel<unknown>,
  vm: FormatterFormViewModel
): IFormatterDataModel<unknown> {
  setOrDelete(model, 'displayName', toOptionalString(vm.displayName));
  setOrDelete(model, 'description', toOptionalString(vm.description));
  return model;
}

function setOrDelete<T extends object, K extends keyof T>(config: T, key: K, value: T[K] | undefined) {
  if (value === undefined) {
    delete config[key];
  } else {
    config[key] = value;
  }
}

function toOptionalString(value: string | undefined): string | undefined {
  return value == null || value === '' ? undefined : value;
}

function toOptionalNumber(value: number | undefined): number | undefined {
  // Number form inputs can yield '' or null when cleared.
  if (value == null || (value as unknown) === '') {
    return undefined;
  }
  return Number(value);
}
